import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAuth?: boolean; // true = nécessite d'être connecté, false = nécessite de ne pas être connecté
}

/**
 * Composant de protection des routes qui gère les redirections automatiques
 * selon l'état d'authentification
 */
export function ProtectedRoute({ children, requireAuth = true }: ProtectedRouteProps) {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Attendre que le chargement de l'auth soit terminé
    if (loading) return;

    if (requireAuth && !user) {
      // Route protégée mais utilisateur non connecté → redirection vers auth
      console.log('🔒 Route protégée, redirection vers /auth');
      navigate('/auth', { replace: true });
    } else if (!requireAuth && user) {
      // Skip auto-redirect if new user signup is in progress
      const isNewUserSignup = sessionStorage.getItem('isNewUserSignup') === 'true';
      
      if (!isNewUserSignup) {
        // Route publique mais utilisateur connecté → redirection vers dashboard
        console.log('✅ Utilisateur connecté, redirection vers /dashboard');
        navigate('/dashboard', { replace: true });
      } else {
        console.log('🚫 Skipping auto-redirect to dashboard - new user onboarding in progress');
      }
    }
  }, [user, loading, navigate, requireAuth]);

  // Afficher un loader pendant la vérification d'auth
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Si on arrive ici, l'utilisateur a le bon statut pour la route
  return <>{children}</>;
}